import { Injectable, inject } from '@angular/core'
import { DealPartyE, DealViewRawBids, DealViewRawCosts, DealViewRawDeal, DealViewRawFiles, DealViewRawInvoices, DealViewRawOffers, DealViewRawSegments, type DealViewRawStatus } from '@tradecafe/types/core'
import { DeepReadonly } from '@tradecafe/types/utils'
import { isEqual } from 'lodash-es'
import { tap } from 'rxjs/operators'
import { AuthApiService } from 'src/api/auth'
import { canChangeParty, canEditDealFormField } from 'src/services/data/deal-view-permissions.service'
import { ModalService } from 'src/shared/modal/modal.service'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { DealFormGroup } from '../deal-form.schema'
import { ChangePartyFormComponent, ChangePartyFormOptions, ChangePartyFormResult } from './change-party-form.component'

type D = DealViewRawDeal & DealViewRawOffers & DealViewRawBids & DealViewRawCosts & DealViewRawFiles & DealViewRawSegments & DealViewRawInvoices & DealViewRawStatus

@Injectable()
export class ChangePartyFormService {
  private readonly AuthApi = inject(AuthApiService)
  private readonly modal = inject(ModalService)
  private readonly toaster = inject(ToasterService)


  showChangeParty(deals: DeepReadonly<D[]>, party: DealPartyE.buyer | DealPartyE.supplier) {
    try {
      deals = sortDealProducts(deals)
    } catch (e) {
      this.toaster.error(`Can't change party. Deal is not OK`, e)
      return undefined
    }

    const editableParty = deals.every(dv => canChangeParty(dv.deal, party, this.AuthApi.currentUser))
    const editablePriceField = party === DealPartyE.buyer ? 'buyerEstPrice' : 'supplierEstPrice'
    const editablePrice = deals.every(dv => canEditDealFormField(dv, editablePriceField, this.AuthApi.currentUser))
    if (!editableParty && !editablePrice) {
      this.toaster.warning('You do not have permission to change the party or the price')
      return undefined
    }

    const expectedProductsCount = deals[0].offers.length
    const sameProductsCount = deals.every(dv => dv.bids.length === expectedProductsCount && dv.offers.length === expectedProductsCount)
    if (!sameProductsCount) {
      this.toaster.warning('All deals must have the same number of products')
      return undefined
    }

    function findProductIds(dv: DeepReadonly<D>) {
      return dv.bids.map(b => b.product)
    }
    const expectedProducts = findProductIds(deals[0])
    const sameProducts = deals.every(dv => isEqual(findProductIds(dv), expectedProducts))
    if (!sameProducts) {
      this.toaster.warning('All deals must have the same set of products')
      return undefined
    }

    return this.show({ deals, party, change: 'party' })
  }

  showChangeFormula(
    dv: DeepReadonly<D>,
    dealFormGroup: DealFormGroup,
    productIndex: number,
    party: DealPartyE.buyer | DealPartyE.supplier,
  ) {
    const dealForm = dealFormGroup.getRawValue()
    // apply form values
    dv = {
      ...dv,
      deal: {
        ...dv.deal,
        buyer_id: dealForm.details.buyerId,
        supplier_id: dealForm.details.supplierId,
        dest_location: dealForm.details.destLocationId,
        origin_location: dealForm.details.originLocationId,
      },
      bids: dealForm.products.map(pf => ({
        ...pf.bid,
        product: pf.productId,
        currency: dealForm.details.buyerCurrencyCode,
        price: pf.buyerEstPrice,
        incoterm: pf.buyerIncotermId,
        formula: pf.buyerFormula,
        weight: {
          ...pf.bid?.weight,
          unit: pf.buyerMeasureId,
        },
        attributes: {
          ...pf.bid?.attributes || {},
          incoterm_location: pf.buyerIncotermLocationId,
        },
      })),
      offers: dealForm.products.map(pf => ({
        ...pf.offer,
        product: pf.productId,
        currency: dealForm.details.supplierCurrencyCode,
        price: pf.supplierEstPrice,
        incoterm: pf.supplierIncotermId,
        formula: pf.supplierFormula,
        weight: {
          ...pf.offer?.weight,
          unit: pf.supplierMeasureId,
        },
        attributes: {
          ...pf.offer?.attributes || {},
          incoterm_location: pf.supplierIncotermLocationId,
        },
      })),
    }
    return this.show({ deals: [dv], party, change: 'formula', productIndex }).pipe(tap(changes => {
      const patch = changes?.[0]
      if (!patch) return
      const { details: { deal_id, ...details } } = patch
      const products = patch.products.map(({ bid_id, offer_id, ...product}) => product)
      dealFormGroup.patchValue({ ...patch, details, products })
    }))
  }

  private show(opts: ChangePartyFormOptions) {
    return this.modal.openDialog<ChangePartyFormComponent, ChangePartyFormOptions, ChangePartyFormResult>(ChangePartyFormComponent, opts, {
      panelClass: ['tc-dialog', 'modal-lg'],
      width: null,
      maxWidth: null,
      autoFocus: false,
    })
  }
}


function sortDealProducts<T extends DeepReadonly<D[]>>(deals: T) {
  // sort bids & offers so bid[i] corresponds to offer[i]
  return deals.map(dv => {
    // check data integrity
    if (dv.bids.length !== dv.offers.length) throw new Error(`${dv.deal.deal_id} has inconsistent bids and offers`)
    if (dv.bids.length !== dv.deal.bids.length) throw new Error(`${dv.deal.deal_id} has inconsistent bids`)
    if (dv.offers.length !== dv.deal.offers.length) throw new Error(`${dv.deal.deal_id} has inconsistent offers`)
    if (dv.bids.some(b => !dv.offers.some(o => o.offer_id === b.offer))) throw new Error(`${dv.deal.deal_id} has bids without corresponding offers`)
    if (dv.offers.some(o => !dv.bids.some(b => b.offer === o.offer_id))) throw new Error(`${dv.deal.deal_id} has offers without corresponding bids`)
    if (dv.deal.bids.some(bidId => !dv.bids.some(b => b.bid_id === bidId))) throw new Error(`${dv.deal.deal_id} has bids without corresponding bids`)
    if (dv.deal.offers.some(offerId => !dv.offers.some(o => o.offer_id === offerId))) throw new Error(`${dv.deal.deal_id} has offers without corresponding offers`)
    if (dv.bids.some(b => !dv.deal.bids.includes(b.bid_id))) throw new Error(`${dv.deal.deal_id} has bids not included in the deal`)
    if (dv.offers.some(o => !dv.deal.offers.includes(o.offer_id))) throw new Error(`${dv.deal.deal_id} has offers not included in the deal`)
    const bids = dv.deal.bids.map(bidsId => dv.bids.find(b => b.bid_id === bidsId));
    const offers = bids.map(b => dv.offers.find(o => o.offer_id === b.offer));
    return { ...dv, bids, offers };
  });
}
